import React, { useState } from "react";
import { motion } from "framer-motion";

// Only needed if you plan to manually call `window.DD.init`.
declare global {
  interface Window {
    DD: any;
  }
}

const FAQItem: React.FC<{ question: string; answer: string; }> = ({ question, answer }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="w-full border border-gray-300 rounded-md p-4 font-alice">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h4 className="text-lg font-semibold text-gray-800">{question}</h4>
        <svg
          className={`w-6 h-6 transform-gpu transition-transform duration-300 ${expanded ? "rotate-90" : "rotate-0"}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
        </svg>
      </div>
      {expanded ? (
        <motion.div
          key="content"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-2 overflow-hidden"
        >
          <p className="text-gray-700">{answer}</p>
        </motion.div>
      ) : null}
    </div>
  );
};

const Matching: React.FC = () => {
  return (
    <section className="bg-gradient-to-b from-[#FAF0E6] to-white text-gray-800 font-alice">
      <section className="py-16 px-8 text-center bg-[#FAF0E6]">
        <h1 className="text-5xl font-alice text-gray-800 mb-4">Make Your Gift Go Further</h1>
        <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
          Many employers offer donation-matching programs that can multiply the impact of your gift. Learn how to take advantage of these opportunities to do even more good.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10 max-w-5xl mx-auto">
        </div>
        <section className="mt-16 max-w-4xl mx-auto text-left">
          <h2 className="text-3xl font-semibold text-gray-800 mb-4 font-alice">
            Matching Gifts
          </h2>
          <p className="text-lg text-gray-700 mb-4">
            Many companies match the donations their employees make to nonprofits. This is a great way to double or even triple the impact of your contribution!
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Use the search tool below to check if your employer offers a matching gift program:
          </p>

          {/* Container for Double the Donation widget */}
          <div id="dd-container" className="mb-8">
            <a
              href="https://doublethedonation.com/matching-grant-resources/matching-gift-basics/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#DAF0F7] hover:text-[#9CAF88]"
            >
              Matching Gift
            </a>{" "}
            and{" "}
            <a
              href="https://doublethedonation.com/matching-grant-resources/volunteer-grant-basics/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#DAF0F7] hover:text-[#9CAF88]"
            >
              Volunteer Grant
            </a>{" "}
            information provided by <br />
            <a href="https://doublethedonation.com">
              <img
                alt="Verified by Double the Donation"
                src="https://doublethedonation.com/api/img/powered-by.png"
                className="mt-2 shadow-md rounded"
              />
            </a>
          </div>
        </section>

        {/* Corporate Giving Programs and FAQs Section */}
        <section className="mt-16 max-w-4xl mx-auto text-left">
          <h2 className="text-3xl font-semibold text-gray-800 mb-4 font-alice">
            Corporate Giving Programs
          </h2>
          <p className="text-lg text-gray-700 mb-4">
            Many companies encourage workplace giving by matching donations, offering volunteer grants, or providing other incentives.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Check if your company participates to maximize your impact!
          </p>

          <h3 className="text-2xl font-semibold text-gray-800 mb-4 font-alice">
            Frequently Asked Questions
          </h3>
          <p className="text-lg text-gray-600 mb-6">
            Here are some of the most common questions we receive about matching gifts and corporate giving.
          </p>
          <div className="bg-white bg-opacity-80 p-6 rounded-lg shadow-md">
            <div className="space-y-4">
              {(() => {
                const faqs = [
                  {
                    question: "What are employee matching gift programs?",
                    answer: "Your company may match your donation, doubling your impact.",
                  },
                  {
                    question: "What are volunteer grant programs?",
                    answer: "Some companies donate extra when you volunteer.",
                  },
                  {
                    question: "How do I request a matching gift or volunteer grant?",
                    answer: "A quick 5-minute form can start the process.",
                  },
                  {
                    question: "How is this information obtained?",
                    answer: "Data is provided by our partner, Double the Donation.",
                  },
                  {
                    question: "What if I still have questions?",
                    answer: "Contact your HR or our team for more details.",
                  },
                ];
                return (
                  <div className="space-y-4">
                    {faqs.map((faq, index) => (
                      <FAQItem key={index} question={faq.question} answer={faq.answer} />
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </section>
      </section>
      <section className="mt-20 bg-[#9CAF88] text-white py-12 px-6 rounded-xl shadow-lg max-w-5xl mx-auto text-center">
        <h2 className="text-3xl font-alice mb-4">Ready to Make an Impact?</h2>
        <p className="text-lg mb-6 max-w-2xl mx-auto">
          Your generosity supports real change. Join us in building sustainable futures for underserved communities.
        </p>
        <a href="/donate" className="inline-block bg-white text-[#9CAF88] font-semibold px-6 py-3 rounded-lg shadow hover:bg-gray-100 transition">
          Donate Now
        </a>
      </section>
    </section>
  );
};

export default Matching;